/**
 * Animated Color Block for Content Collage
 * Applies color cycling animation to specific collage blocks
 */

(function() {
  'use strict';

  // Color configuration
  const COLORS = [
    { bg: '#F0861D', text: '#ffffff' },
    { bg: '#16395C', text: '#ffffff' },
    { bg: '#5A7B7D', text: '#ffffff' },
    { bg: '#C7BFD0', text: '#ffffff' }
  ];

  const CYCLE_DURATION = 3000; // 3 seconds per color (for first 3 colors)
  let currentColorIndex = 0;
  let colorCycleTimeouts = [];

  /**
   * Apply color to a block and its text elements
   */
  function applyColor(block, colorIndex) {
    const color = COLORS[colorIndex];

    // Set background color (override inline style)
    block.style.setProperty('background-color', color.bg, 'important');
    block.style.setProperty('--custom-background', color.bg, 'important');

    // Set text color
    block.style.setProperty('color', color.text, 'important');

    // Update text elements within the block
    const textElements = block.querySelectorAll('.promotion__heading, .promotion__description, .promotion__description p');
    textElements.forEach(element => {
      element.style.setProperty('color', color.text, 'important');
    });

    // Slightly transparent description
    const description = block.querySelector('.promotion__description');
    if (description) {
      description.style.setProperty('opacity', '0.85', 'important');
    }
  }

  /**
   * Clear all scheduled color change timeouts
   */
  function clearColorTimeouts() {
    colorCycleTimeouts.forEach(timeout => clearTimeout(timeout));
    colorCycleTimeouts = [];
  }

  /**
   * Schedule color changes based on video duration
   */
  function scheduleColorChanges(animatedBlocks, videoDuration) {
    // Clear any existing timeouts
    clearColorTimeouts();

    // Reset to first color
    currentColorIndex = 0;
    animatedBlocks.forEach(block => {
      applyColor(block, currentColorIndex);
    });

    // Calculate when each color should change
    // Colors 1, 2, 3 are 3 seconds each
    // Color 4 duration = remaining time until video loops
    const color2Time = CYCLE_DURATION; // 3s
    const color3Time = CYCLE_DURATION * 2; // 6s
    const color4Time = CYCLE_DURATION * 3; // 9s
    const color4Duration = Math.max(1000, videoDuration * 1000 - color4Time); // Remaining time (min 1s)

    // Schedule color 2 at 3s
    colorCycleTimeouts.push(setTimeout(() => {
      currentColorIndex = 1;
      animatedBlocks.forEach(block => applyColor(block, currentColorIndex));
    }, color2Time));

    // Schedule color 3 at 6s
    colorCycleTimeouts.push(setTimeout(() => {
      currentColorIndex = 2;
      animatedBlocks.forEach(block => applyColor(block, currentColorIndex));
    }, color3Time));

    // Schedule color 4 at 9s (will last until video loops)
    colorCycleTimeouts.push(setTimeout(() => {
      currentColorIndex = 3;
      animatedBlocks.forEach(block => applyColor(block, currentColorIndex));
    }, color4Time));
  }

  /**
   * Start color cycling synchronized with video
   */
  function startColorCycling() {
    // Find all blocks with the animated-color-block class
    let animatedBlocks = document.querySelectorAll('.content-collage__block.animated-color-block');

    // If no blocks with class found, auto-target first text block in collage
    if (animatedBlocks.length === 0) {
      const firstTextBlock = document.querySelector('.content-collage__block-text');
      if (firstTextBlock) {
        animatedBlocks = [firstTextBlock];
      }
    }

    if (animatedBlocks.length === 0) {
      return;
    }

    // Find the video element in the collage
    const video = document.querySelector('.content-collage__block-video video');

    if (video) {
      // Wait for video metadata to load so we have duration
      const startAnimation = () => {
        const videoDuration = video.duration;

        if (!videoDuration || isNaN(videoDuration)) {
          console.warn('[Collage Animation] Video duration not available');
          return;
        }

        // Schedule color changes based on actual video duration
        scheduleColorChanges(animatedBlocks, videoDuration);
      };

      // Listen for when video metadata is loaded
      if (video.readyState >= 1) {
        // Metadata already loaded
        startAnimation();
      } else {
        video.addEventListener('loadedmetadata', startAnimation, { once: true });
      }

      // Restart animation when video loops back to start
      video.addEventListener('seeked', () => {
        if (video.currentTime < 1 && video.duration) {
          // Video has looped - reschedule everything
          scheduleColorChanges(animatedBlocks, video.duration);
        }
      });

      // Also catch the 'ended' event as a backup for loop detection
      video.addEventListener('ended', () => {
        if (video.loop && video.duration) {
          scheduleColorChanges(animatedBlocks, video.duration);
        }
      });
    } else {
      // No video found, use default 3s intervals for all colors
      const defaultDuration = CYCLE_DURATION * 4 / 1000; // 12 seconds total
      scheduleColorChanges(animatedBlocks, defaultDuration);

      // Restart animation in a loop
      setInterval(() => {
        scheduleColorChanges(animatedBlocks, defaultDuration);
      }, defaultDuration * 1000);
    }
  }

  /**
   * Initialize when DOM is ready
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startColorCycling);
    } else {
      startColorCycling();
    }
  }

  // Initialize
  init();

  // Re-initialize for Shopify theme editor
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      setTimeout(startColorCycling, 100);
    });
  }

})();
